import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  SettingsContainer,
  SettingsHeader,
  SettingsLayout,
  SettingsSidebar,
  SidebarHeader,
  SidebarNav,
  SidebarFooter,
  SidebarItem,
  SettingsMainContent,
  SettingsCard,
  StatusPill,
  FormGrid,
  FormGroup,
  Label,
  Input,
  PasswordInputWrapper,
  CheckboxWrapper,
  TestPanel,
  ActionButton,
  AiFeatureNotice,
} from './styles'
import {
  FiMail,
  FiServer,
  FiLock,
  FiCheckCircle,
  FiSend,
  FiEye,
  FiEyeOff,
  FiCpu,
  FiShield,
  FiSettings,
  FiRefreshCw,
  FiArrowLeft,
} from 'react-icons/fi'
import { useAuth } from '../../context/AuthContext'
import api from '../../services/axios'

export function AdminSettings() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isSuperAdmin = user?.role === 'administrador'

  const [activeTab, setActiveTab] = useState<'smtp' | 'ai'>('smtp')

  // SMTP States
  const [host, setHost] = useState('')
  const [port, setPort] = useState(587)
  const [smtpUser, setSmtpUser] = useState('')
  const [pass, setPass] = useState('')
  const [from, setFrom] = useState('')
  const [secure, setSecure] = useState(false)
  const [hasPass, setHasPass] = useState(false)
  const [configured, setConfigured] = useState(false)

  const [showPass, setShowPass] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState(false)

  useEffect(() => {
    fetchSmtpConfig()
  }, [])

  const fetchSmtpConfig = async () => {
    try {
      setLoading(true)
      const res = await api.get('/admin/config/smtp')
      if (res.data) {
        setHost(res.data.host || '')
        setPort(res.data.port || 587)
        setSmtpUser(res.data.user || '')
        setFrom(res.data.from || '')
        setSecure(Boolean(res.data.secure))
        setHasPass(Boolean(res.data.hasPass))
        setConfigured(Boolean(res.data.configured))
        setTestEmail(res.data.user || user?.email || '')
      }
    } catch (err) {
      console.error('Erro ao buscar configurações SMTP:', err)
      toast.error('Erro ao carregar configurações SMTP.')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveSmtp = async () => {
    if (!host || !smtpUser || !port) {
      toast.warn('Preencha Host, Porta e Usuário/E-mail.')
      return
    }

    if (!hasPass && !pass) {
      toast.warn('A senha do SMTP é obrigatória no primeiro cadastro.')
      return
    }

    try {
      setLoading(true)
      const portNum = Number(port)
      const isPort465 = portNum === 465
      await api.post('/admin/config/smtp', {
        host,
        port: portNum,
        user: smtpUser,
        pass: pass || undefined,
        from,
        secure: isPort465 ? true : (secure && portNum !== 587),
      })
      toast.success('Configurações SMTP salvas com sucesso!')
      setHasPass(true)
      setConfigured(true)
      setPass('')
    } catch (err: any) {
      console.error('Erro ao salvar SMTP:', err)
      const msg = err.response?.data?.error || 'Erro ao salvar configurações SMTP.'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleTestSmtp = async () => {
    if (!host || !smtpUser || !port) {
      toast.warn('Preencha os dados do servidor antes de testar.')
      return
    }

    if (!hasPass && !pass) {
      toast.warn('Digite a senha para poder realizar o teste de conexão.')
      return
    }

    if (!testEmail || !testEmail.includes('@')) {
      toast.warn('Informe um e-mail válido para receber o teste.')
      return
    }

    try {
      setTesting(true)
      const portNum = Number(port)
      const isPort465 = portNum === 465
      const res = await api.post('/admin/config/smtp/testar', {
        host,
        port: portNum,
        user: smtpUser,
        pass: pass || undefined,
        from,
        secure: isPort465 ? true : false,
        emailDestino: testEmail,
      })

      toast.success(res.data.message || 'Teste realizado com sucesso!')
    } catch (err: any) {
      console.error('Falha no teste SMTP:', err)
      const msg = err.response?.data?.error || 'Falha ao autenticar ou enviar e-mail de teste.'
      toast.error(msg)
    } finally {
      setTesting(false)
    }
  }

  if (!isSuperAdmin) {
    return (
      <SettingsContainer>
        <SettingsCard>
          <h3 style={{ color: '#ef4444' }}>Acesso Restrito</h3>
          <p style={{ color: '#94a3b8' }}>Esta tela é restrita para o Administrador (Super Admin) do sistema.</p>
        </SettingsCard>
      </SettingsContainer>
    )
  }

  return (
    <SettingsContainer>
      <SettingsLayout>
        <SettingsSidebar>
          <SidebarHeader>
            <div className="brand-group">
              <div className="brand-icon">
                <FiSettings />
              </div>
              <div className="brand-text">
                <span>Configurações</span>
                <small>Painel do Sistema</small>
              </div>
            </div>

           
          </SidebarHeader>

          <SidebarNav>
            <SidebarItem $active={activeTab === 'smtp'} onClick={() => setActiveTab('smtp')}>
              <div className="icon-box">
                <FiMail />
              </div>
              <div className="text-box">
                <span className="title">Servidor de E-mail</span>
                <span className="desc">SMTP & notificações</span>
              </div>
            </SidebarItem>

            <SidebarItem $active={activeTab === 'ai'} onClick={() => setActiveTab('ai')}>
              <div className="icon-box">
                <FiCpu />
              </div>
              <div className="text-box">
                <span className="title">Inteligência Artificial</span>
                <span className="desc">Google Gemini & Insights</span>
              </div>
            </SidebarItem>
          </SidebarNav>

          <SidebarFooter>
            <div className="user-avatar">
              {user?.name?.slice(0, 2).toUpperCase() || 'AD'}
            </div>
            <div className="user-info">
              <span className="name">{user?.name || user?.nickname || 'Super Admin'}</span>
              <span className="role">Administrador</span>
            </div>
          </SidebarFooter>
        </SettingsSidebar>

        <SettingsMainContent>
          <SettingsHeader>
            <div className="title-group">
              <h2>
                <FiSettings /> Painel de Configurações do Sistema
              </h2>
              <p>Gerencie parâmetros de infraestrutura, e-mail (SMTP) e inteligência artificial.</p>
            </div>

            <div className="admin-badge">
              <FiShield /> Super Admin
            </div>
          </SettingsHeader>

          {/* ABA 1: CONFIGURAÇÃO DE SMTP */}
          {activeTab === 'smtp' && (
        <SettingsCard>
          <div className="card-header">
            <div className="header-text">
              <h3>
                <FiServer /> Servidor de Envio de E-mails (SMTP)
              </h3>
              <p>Utilizado para redefinição de senhas, alertas automáticos e notificações operacionais do GID.</p>
            </div>

            <StatusPill $configured={configured}>
              {configured ? '✔ Configurado e Ativo' : '⚠ Pendente de Configuração'}
            </StatusPill>
          </div>

          <FormGrid>
            <FormGroup>
              <Label>Servidor SMTP (Host)</Label>
              <Input
                type="text"
                placeholder="Ex: smtp.gmail.com ou mail.grupogsa.com.br"
                value={host}
                onChange={(e) => setHost(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>Porta</Label>
              <Input
                type="number"
                placeholder="587 ou 465"
                value={port}
                onChange={(e) => {
                  const val = Number(e.target.value)
                  setPort(val)
                  if (val === 465) setSecure(true)
                  if (val === 587) setSecure(false)
                }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Usuário / E-mail de Autenticação</Label>
              <Input
                type="email"
                placeholder="Ex: notificacoes@grupogsa.com.br"
                value={smtpUser}
                onChange={(e) => setSmtpUser(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>
                Senha do SMTP {hasPass && <span style={{ color: '#10b981', fontWeight: 600 }}>(Criptografada AES-256-GCM em Banco)</span>}
              </Label>
              <PasswordInputWrapper>
                <Input
                  type={showPass ? 'text' : 'password'}
                  placeholder={hasPass ? 'Deixe vazio para manter a atual' : 'Digite a senha do e-mail'}
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                />
                <button type="button" className="toggle-btn" onClick={() => setShowPass(!showPass)}>
                  {showPass ? <FiEyeOff /> : <FiEye />}
                </button>
              </PasswordInputWrapper>
            </FormGroup>

            <FormGroup style={{ gridColumn: 'span 2' }}>
              <Label>Nome do Remetente (From - Opcional)</Label>
              <Input
                type="text"
                placeholder='Ex: GID Notificações <notificacoes@grupogsa.com.br>'
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </FormGroup>
          </FormGrid>

          <CheckboxWrapper>
            <input
              type="checkbox"
              id="smtp-secure-toggle"
              checked={Number(port) === 465 ? true : secure && Number(port) !== 587}
              onChange={(e) => {
                const isChecked = e.target.checked
                if (isChecked && Number(port) === 587) {
                  toast.info('A porta 587 usa STARTTLS automático. Conexão direta SSL/TLS deve ser usada na porta 465.')
                  setSecure(false)
                } else {
                  setSecure(isChecked)
                }
              }}
            />
            <label htmlFor="smtp-secure-toggle">
              Conexão Segura Direta SSL/TLS (Ativa apenas para porta 465. A porta 587 já negocia STARTTLS automaticamente).
            </label>
          </CheckboxWrapper>

          {/* PAINEL DE TESTE EM TEMPO REAL */}
          <TestPanel>
            <div className="test-title">
              <FiSend /> Testar Conexão em Tempo Real
            </div>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 12px 0' }}>
              Valide as credenciais antes de salvar. Um e-mail de confirmação será disparado imediatamente.
            </p>
            <div className="test-row">
              <Input
                type="email"
                placeholder="E-mail que receberá a mensagem de teste"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
              <ActionButton
                type="button"
                $variant="accent"
                onClick={handleTestSmtp}
                disabled={testing || loading}
              >
                {testing ? <FiRefreshCw className="spinner" /> : <FiSend />}
                {testing ? 'Testando Conexão...' : 'Testar Envio'}
              </ActionButton>
            </div>
          </TestPanel>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <ActionButton
              type="button"
              $variant="secondary"
              onClick={fetchSmtpConfig}
              disabled={loading}
            >
              Descartar / Recarregar
            </ActionButton>
            <ActionButton
              type="button"
              $variant="primary"
              onClick={handleSaveSmtp}
              disabled={loading || testing}
            >
              {loading ? <FiRefreshCw className="spinner" /> : <FiCheckCircle />}
              {loading ? 'Salvando...' : 'Salvar Configurações'}
            </ActionButton>
          </div>
        </SettingsCard>
      )}

      {/* ABA 2: INTELIGÊNCIA ARTIFICIAL */}
      {activeTab === 'ai' && (
        <SettingsCard>
          <AiFeatureNotice>
            <div className="glow-badge">✨ Módulo Inteligência Artificial (IA)</div>
            <h3>Assistente Cognitivo & Análise Preditiva de Sellout</h3>
            <p>
              Este módulo está sendo arquitetado para integrar a API do <strong>Google Gemini</strong> ao ecossistema
              GID. Ele permitirá cruzamentos automáticos de rupturas de estoque, previsões de demanda por distribuidor e
              geração de relatórios executivos em linguagem natural.
            </p>

            <FormGrid style={{ marginTop: '16px', maxWidth: '800px' }}>
              <FormGroup>
                <Label>Modelo Gemini Ativo</Label>
                <Input type="text" value="Gemini 2.0 Flash (Recomendado para velocidade e precisão)" disabled />
              </FormGroup>

              <FormGroup>
                <Label>Chave de API (Gemini API Key)</Label>
                <Input type="password" value="••••••••••••••••••••••••••••••••" disabled />
              </FormGroup>
            </FormGrid>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <StatusPill $configured={true}>✔ Módulo Disponível na Central de Integrações</StatusPill>
              <ActionButton
                type="button"
                $variant="primary"
                onClick={() => navigate('/integracoes')}
              >
                <FiCpu /> Abrir Central de API Keys, IA GISA & Relatórios Diários
              </ActionButton>
            </div>
          </AiFeatureNotice>
        </SettingsCard>
      )}
        </SettingsMainContent>
      </SettingsLayout>
    </SettingsContainer>
  )
}

export default AdminSettings

